/**
 * Email + 4-digit PIN auth gate, shown before the main menu.
 * Pure DOM, sits as a fullscreen .screen.modal over the WebGL canvas.
 */

import { BRAND } from "../content/brand";

export type AuthMode = "signup" | "login";

export interface AuthSubmit {
  mode: AuthMode;
  email: string;
  pin: string;
}

export interface AuthResult {
  ok: boolean;
  error?: string;
  until?: string;
}

export class Auth {
  private el: HTMLDivElement;
  private emailInput: HTMLInputElement;
  private pinInput: HTMLInputElement;
  private title: HTMLHeadingElement;
  private submitBtn: HTMLButtonElement;
  private toggleLink: HTMLAnchorElement;
  private errorMsg: HTMLDivElement;
  private mode: AuthMode = "signup";
  private onSubmit: (d: AuthSubmit) => Promise<AuthResult> = async () => ({ ok: true });
  private onGuest: () => void = () => {};

  constructor(parent: HTMLElement) {
    this.el = document.createElement("div");
    this.el.className = "screen modal auth-screen hidden";
    this.el.innerHTML = `
      <div></div>
      <div class="title">
        <h1>${BRAND.artistHtml}</h1>
        <h2 id="auth-title">Create Account</h2>
        <p class="tag">Save your coins, missions, and unlocked cars to your account.</p>
      </div>
      <form class="auth-form" id="auth-form" autocomplete="on" novalidate>
        <label class="auth-field">
          <span>Email</span>
          <input type="email" id="auth-email" name="email"
                 inputmode="email" autocomplete="email"
                 autocapitalize="none" autocorrect="off" spellcheck="false"
                 placeholder="you@example.com" required />
        </label>
        <label class="auth-field">
          <span>4-digit PIN</span>
          <input type="password" id="auth-pin" name="pin"
                 inputmode="numeric" autocomplete="one-time-code"
                 pattern="[0-9]{4}" maxlength="4"
                 placeholder="••••" required />
        </label>
        <div class="auth-error" id="auth-error" hidden></div>
        <div class="btn-row auth-btn-row">
          <button type="submit" class="btn" id="auth-submit">Create Account</button>
          <button type="button" class="btn secondary" id="auth-guest">Play as Guest</button>
        </div>
        <p class="auth-toggle">
          <a id="auth-toggle" href="#">Already have an account? Sign in</a>
        </p>
        <p class="auth-consent">
          By continuing you agree to receive product updates by email.
          Guests play locally and won't sync progress across devices.
        </p>
      </form>
    `;
    parent.appendChild(this.el);

    this.emailInput = this.el.querySelector("#auth-email") as HTMLInputElement;
    this.pinInput = this.el.querySelector("#auth-pin") as HTMLInputElement;
    this.title = this.el.querySelector("#auth-title") as HTMLHeadingElement;
    this.submitBtn = this.el.querySelector("#auth-submit") as HTMLButtonElement;
    this.toggleLink = this.el.querySelector("#auth-toggle") as HTMLAnchorElement;
    this.errorMsg = this.el.querySelector("#auth-error") as HTMLDivElement;

    this.toggleLink.addEventListener("click", (e) => {
      e.preventDefault();
      this.setMode(this.mode === "signup" ? "login" : "signup");
    });

    (this.el.querySelector("#auth-guest") as HTMLButtonElement).addEventListener(
      "click",
      () => this.onGuest(),
    );

    // PIN: strip non-digits and cap at 4.
    this.pinInput.addEventListener("input", () => {
      const cleaned = this.pinInput.value.replace(/\D/g, "").slice(0, 4);
      if (cleaned !== this.pinInput.value) this.pinInput.value = cleaned;
    });

    const form = this.el.querySelector("#auth-form") as HTMLFormElement;
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.handleSubmit();
    });
  }

  private async handleSubmit(): Promise<void> {
    const email = this.emailInput.value.trim();
    const pin = this.pinInput.value;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showError("Enter a valid email.");
      this.emailInput.focus();
      return;
    }
    if (!/^[0-9]{4}$/.test(pin)) {
      this.showError("PIN must be 4 digits.");
      this.pinInput.focus();
      return;
    }
    this.setBusy(true);
    try {
      const r = await this.onSubmit({ mode: this.mode, email, pin });
      if (!r.ok) this.showError(this.errorText(r.error, r.until));
    } catch {
      this.showError("Network error. Try again.");
    } finally {
      this.setBusy(false);
    }
  }

  private setMode(m: AuthMode): void {
    this.mode = m;
    this.title.textContent = m === "signup" ? "Create Account" : "Sign In";
    this.submitBtn.textContent = m === "signup" ? "Create Account" : "Sign In";
    this.toggleLink.textContent =
      m === "signup" ? "Already have an account? Sign in" : "New here? Create an account";
    this.hideError();
  }

  private showError(text: string): void {
    this.errorMsg.textContent = text;
    this.errorMsg.hidden = false;
  }
  private hideError(): void {
    this.errorMsg.hidden = true;
  }
  private setBusy(busy: boolean): void {
    this.submitBtn.disabled = busy;
    this.emailInput.disabled = busy;
    this.pinInput.disabled = busy;
    this.submitBtn.textContent = busy
      ? "Working…"
      : this.mode === "signup"
        ? "Create Account"
        : "Sign In";
  }
  private errorText(code: string | undefined, until?: string): string {
    switch (code) {
      case "email_taken":
        return "That email already has an account. Sign in instead.";
      case "invalid_email":
        return "Enter a valid email.";
      case "invalid_pin":
        return "PIN must be 4 digits.";
      case "invalid_credentials":
        return "Incorrect email or PIN.";
      case "locked": {
        if (until) {
          const mins = Math.max(1, Math.ceil((new Date(until).getTime() - Date.now()) / 60000));
          return `Too many attempts. Try again in ~${mins} min.`;
        }
        return "Too many attempts. Try again later.";
      }
      case "db_not_configured":
        return "Server isn't connected to the database yet. Check Vercel env vars.";
      case "schema_missing":
        return "Database tables are missing. Run db/schema.sql in the Neon SQL editor.";
      case "server_error":
        return "Server error. Please try again in a moment.";
      case "network":
        return "Network error. Check your connection and try again.";
      default:
        return "Something went wrong. Try again.";
    }
  }

  show(
    onSubmit: (d: AuthSubmit) => Promise<AuthResult>,
    onGuest?: () => void,
  ): void {
    this.onSubmit = onSubmit;
    this.onGuest = onGuest ?? (() => {});
    this.setMode("signup");
    this.emailInput.value = "";
    this.pinInput.value = "";
    this.hideError();
    this.el.classList.remove("hidden");
    // Small delay so iOS Safari doesn't fight the focus.
    window.setTimeout(() => this.emailInput.focus(), 60);
  }

  hide(): void {
    this.el.classList.add("hidden");
  }
}
